import { motion } from 'framer-motion';
import { Target, Users, Lightbulb, TrendingUp, Award, Heart } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../components/MarketingLayout';
import Breadcrumb from '../components/Breadcrumb';
import PageHero from '../components/PageHero';
import { getPageHeroImageUrl } from '../config/cloudflareAssets';

export default function AboutPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);

  return (
    <MarketingLayout>
      <div className="public-breadcrumb">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb items={[{ label: 'About' }]} />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        title="A clearer connection between ambition and action."
        subtitle="We built StratPlan to bring direction, business analysis, people and delivery into one shared view—so your strategy can guide the work that happens next."
      >
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/sign-up" className="public-button public-button--primary">
            Start my strategic plan
          </Link>
          <Link to="/strategic-framework" className="public-button public-button--secondary">
            Explore our approach
          </Link>
        </div>
      </PageHero>

      {/* Mission Section */}
      <section className="public-section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="public-section-heading text-center"
            >
              <h2 className="text-4xl font-bold mb-6">Make strategy useful every day.</h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                A good plan gives people a reason for the work they do. StratPlan connects your
                vision and business analysis to priorities, ownership and execution. Instead of
                rebuilding the story across documents and spreadsheets, your team can see what
                matters, how decisions connect and where to go next.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="public-section public-section--tint">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="public-section-heading text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">The principles behind the platform.</h2>
            <p className="text-xl text-gray-600">
              Clear thinking. Shared context. Practical next steps.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: Target,
                title: 'Clarity',
                description:
                  'We believe in making complex strategic concepts simple and actionable.',
              },
              {
                icon: Users,
                title: 'Collaboration',
                description: 'Strategy is a team sport. We build tools that bring people together.',
              },
              {
                icon: Lightbulb,
                title: 'Innovation',
                description:
                  'We use AI to help you explore possibilities, while keeping decisions in your hands.',
              },
              {
                icon: TrendingUp,
                title: 'Impact',
                description:
                  'We connect planning to the projects and tasks that put your decisions into practice.',
              },
              {
                icon: Award,
                title: 'Excellence',
                description:
                  'We make room for detailed thinking without making the planning process harder to navigate.',
              },
              {
                icon: Heart,
                title: 'Customer Success',
                description:
                  "Your success is our success. We're here to help you achieve your goals.",
              },
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="public-card p-8"
              >
                <div className="public-icon w-16 h-16 flex items-center justify-center mb-5">
                  <value.icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="public-section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold mb-6 text-center">Why we built StratPlan.</h2>
              <div className="public-prose prose prose-lg max-w-none">
                <p className="mb-4">
                  A strategy carries more value when people can follow the reasoning behind it. The
                  vision, market insights, choices and work all need a place in the same story—not
                  just a collection of separate documents.
                </p>
                <p className="mb-4">
                  We built StratPlan around that connection. You can examine the market with PESTEL
                  and Five Forces, understand your capabilities with SWOT, and use those insights to
                  shape strategic priorities. Your decisions stay connected to the company context
                  that informed them.
                </p>
                <p className="mb-4">
                  When it is time to act, the plan can connect to people, projects and tasks. Visual
                  organization charts help make ownership clearer, while AI assistance helps you
                  draft direction, explore options and break work into practical steps.
                </p>
                <p>
                  StratPlan brings the whole journey together: establish your foundation, understand
                  your business, choose your priorities and organize delivery. The connection
                  between those steps is what makes your plan useful beyond the planning session.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="public-cta">
        <div className="container mx-auto px-4 text-center">
          <motion.div initial={false} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold mb-5">
              Give your ambition a place to become action.
            </h2>
            <p className="text-lg mb-7 max-w-2xl mx-auto">
              Build your direction, connect your people and know what comes next.
            </p>
            <Link to="/sign-up" className="public-button public-button--primary">
              Start my strategic plan
            </Link>
          </motion.div>
        </div>
      </section>
    </MarketingLayout>
  );
}
